import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Lock, User, Loader2, AlertCircle } from "lucide-react";

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError("Veuillez remplir tous les champs");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const user = await login(username, password);
      if (user.role === "admin") {
        navigate("/admin");
      } else {
        navigate("/");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur de connexion";
      setError(msg);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-white flex" data-testid="login-page">
      {/* Left - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-zinc-950 relative overflow-hidden items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-transparent" />
        <div className="relative z-10 px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center mb-8">
              <Mail className="w-7 h-7 text-white" />
            </div>
            <h1
              className="text-5xl font-black text-white tracking-tight leading-tight mb-4"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              netlinkMailing
            </h1>
            <p className="text-lg text-zinc-400 leading-relaxed max-w-md">
              Plateforme d'emailing personnalisé. Envoyez des campagnes ciblées à grande échelle.
            </p>
            <div className="mt-12 flex gap-8">
              <div>
                <p className="text-3xl font-black text-white">100%</p>
                <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Personnalisé</p>
              </div>
              <div>
                <p className="text-3xl font-black text-white">SMTP</p>
                <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Multi-config</p>
              </div>
              <div>
                <p className="text-3xl font-black text-white">Sécurisé</p>
                <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Par utilisateur</p>
              </div>
            </div>
          </motion.div>
        </div>
        {/* Decorative circles */}
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full border border-zinc-800/50" />
        <div className="absolute -bottom-16 -right-16 w-64 h-64 rounded-full border border-zinc-800/30" />
        <div className="absolute top-16 right-16 w-32 h-32 rounded-full bg-primary/10 blur-3xl" />
      </div>

      {/* Right - Login Form */}
      <div className="flex-1 flex items-center justify-center px-6 lg:px-16">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="w-full max-w-sm"
        >
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <h1
              className="text-xl font-bold text-zinc-900"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              netlinkMailing
            </h1>
          </div>

          <div className="mb-8">
            <h2
              className="text-2xl font-bold text-zinc-900 tracking-tight"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Connexion
            </h2>
            <p className="text-sm text-zinc-500 mt-2">
              Entrez vos identifiants pour accéder à votre espace
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm"
                data-testid="login-error"
              >
                <AlertCircle className="w-4 h-4 shrink-0" />
                {error}
              </motion.div>
            )}

            <div className="space-y-2">
              <Label htmlFor="username" className="text-sm font-medium text-zinc-700 flex items-center gap-2">
                <User className="w-3.5 h-3.5" />
                Nom d'utilisateur
              </Label>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Votre identifiant"
                autoComplete="username"
                className="h-11"
                data-testid="login-username"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-zinc-700 flex items-center gap-2">
                <Lock className="w-3.5 h-3.5" />
                Mot de passe
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Votre mot de passe"
                autoComplete="current-password"
                className="h-11"
                data-testid="login-password"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-11 rounded-full text-sm font-semibold gap-2 hover:scale-[1.02] active:scale-[0.98] transition-transform"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Lock className="w-4 h-4" />
              )}
              {loading ? "Connexion..." : "Se connecter"}
            </Button>
          </form>

          <p className="text-xs text-zinc-400 text-center mt-8">
            Contactez votre administrateur pour obtenir vos identifiants
          </p>
        </motion.div>
      </div>
    </div>
  );
}
